/**
 * Аккаунты Airbnb и Booking — для администратора.
 *
 * Пароль после сохранения не показывается никому, включая администратора:
 * поле только для замены. Сотрудник его тоже не видит — оболочка
 * подставляет вход сама.
 */

import { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, KeyRound, Pencil, Plus, Trash2, UserPlus, X } from 'lucide-react';
import type { StayAccountDto, StayPersonDto, StayPlatform } from '@tw/shared-types';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSession } from '../../state/session.tsx';
import { describeError } from '../../lib/errors.ts';
import { cn } from '@/lib/utils';
import { EmptyState, FormField, INPUT, PlatformMark, TEXTAREA } from './bits.tsx';
import { PLATFORM } from './meta.ts';

interface Props {
  onChanged: () => void;
}

export function AccountsPanel({ onChanged }: Props) {
  const { api } = useSession();
  const [items, setItems] = useState<StayAccountDto[] | null>(null);
  const [users, setUsers] = useState<StayPersonDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<StayAccountDto | 'new' | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      // Кандидаты — те, у кого есть модуль (без стажёров): сервер всё равно
      // откажет выдать аккаунт роли без доступа к «Апартаментам».
      const [accounts, people] = await Promise.all([api.stayAccounts(), api.stayPeople()]);
      setItems(accounts.items);
      setUsers(people.items);
    } catch (err) {
      setError(describeError(err));
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (err) {
      setError(describeError(err));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="max-w-[70ch] text-[13px] leading-relaxed text-muted-foreground">
          Общие аккаунты компании. Сотрудник с доступом открывает аккаунт во вкладке «Браузер» — вход подставляется сам, пароля он
          не видит. Каждая выдача пароля оболочке записывается в аудит.
        </p>
        <Button size="sm" className="flex-none rounded-full" onClick={() => setEditing('new')}>
          <Plus className="size-4" /> Аккаунт
        </Button>
      </div>

      {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

      {items && items.length === 0 && (
        <EmptyState icon={<KeyRound className="size-6" />} title="Аккаунтов пока нет">
          Подключите аккаунт Airbnb или Booking и выдайте к нему доступ менеджерам.
        </EmptyState>
      )}

      <div className="grid gap-3.5 lg:grid-cols-2">
        {(items ?? []).map((a) => {
          const granted = new Set((a.access ?? []).map((x) => x.id));
          const candidates = users.filter((u) => !granted.has(u.id));
          return (
            <div key={a.id} className={cn('flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 shadow-sm', !a.enabled && 'opacity-60')}>
              <div className="flex items-start gap-3">
                <PlatformMark platform={a.platform} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-semibold text-heading">{a.title}</p>
                  <p className="truncate text-[12px] text-muted-foreground">
                    {PLATFORM[a.platform].label} · {a.login}
                    {!a.hasPassword && <span className="text-warning"> · пароль не задан</span>}
                  </p>
                </div>
                <label className="flex items-center gap-1.5 text-[12px] text-soft">
                  <input type="checkbox" checked={a.enabled} onChange={(e) => void act(() => api.updateStayAccount(a.id, { enabled: e.target.checked }))} />
                  включён
                </label>
                <Button variant="ghost" size="icon-sm" aria-label="Изменить" onClick={() => setEditing(a)}>
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Удалить"
                  className="hover:text-destructive"
                  onClick={() => {
                    if (!window.confirm(`Удалить аккаунт «${a.title}»? Брони по нему останутся, но без ссылки на аккаунт.`)) return;
                    void act(() => api.deleteStayAccount(a.id));
                  }}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
              {a.note && <p className="rounded-xl bg-secondary/60 px-3 py-2 text-[12px] text-soft">{a.note}</p>}

              <div className="flex flex-col gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Доступ</p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {(a.access ?? []).length === 0 && <span className="text-[12px] text-muted-foreground">ни у кого — кроме администраторов</span>}
                  {(a.access ?? []).map((u) => (
                    <span key={u.id} className="inline-flex items-center gap-1 rounded-full bg-secondary py-1 pl-2.5 pr-1 text-[12px] text-foreground">
                      {u.displayName}
                      <button
                        type="button"
                        aria-label={`Забрать доступ у ${u.displayName}`}
                        className="grid size-4 place-items-center rounded-full border-0 bg-transparent p-0 text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
                        onClick={() => void act(() => api.revokeStayAccount(a.id, u.id))}
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                  {candidates.length > 0 && (
                    <label className="relative inline-flex items-center">
                      <UserPlus className="pointer-events-none absolute left-2 size-3.5 text-brand" />
                      <select
                        className="h-7 cursor-pointer appearance-none rounded-full border border-dashed border-brand/50 bg-transparent py-0 pl-7 pr-7 text-[12px] font-medium leading-[26px] text-brand outline-none hover:bg-primary/5"
                        value=""
                        onChange={(e) => e.target.value && void act(() => api.grantStayAccount(a.id, e.target.value))}
                      >
                        <option value="">Выдать доступ</option>
                        {candidates.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.displayName}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AccountDialog
        account={editing === 'new' ? null : editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void load();
          onChanged();
        }}
      />
    </div>
  );
}

function AccountDialog({
  account,
  open,
  onClose,
  onSaved,
}: {
  account: StayAccountDto | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { api } = useSession();
  const [platform, setPlatform] = useState<StayPlatform>('AIRBNB');
  const [title, setTitle] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPlatform(account?.platform ?? 'AIRBNB');
    setTitle(account?.title ?? '');
    setLogin(account?.login ?? '');
    setPassword('');
    setShowPassword(false);
    setNote(account?.note ?? '');
    setError(null);
  }, [open, account]);

  const submit = async () => {
    if (!title.trim() || !login.trim()) return setError('Название и логин обязательны');
    setSaving(true);
    setError(null);
    try {
      if (account) {
        await api.updateStayAccount(account.id, {
          title: title.trim(),
          login: login.trim(),
          note: note.trim() || null,
          ...(password ? { password } : {}),
        });
      } else {
        await api.createStayAccount({
          platform,
          title: title.trim(),
          login: login.trim(),
          note: note.trim() || null,
          ...(password ? { password } : {}),
        });
      }
      onSaved();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{account ? account.title : 'Новый аккаунт'}</DialogTitle>
          <DialogDescription>Пароль хранится зашифрованным и после сохранения не показывается.</DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3">
          {!account && (
            <div className="grid grid-cols-2 gap-2">
              {(['AIRBNB', 'BOOKING'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlatform(p)}
                  className={cn(
                    'flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-[14px] font-medium transition-colors',
                    platform === p ? 'border-brand bg-primary/5 text-heading ring-2 ring-brand/15' : 'border-border text-soft hover:border-brand/50',
                  )}
                >
                  <PlatformMark platform={p} />
                  {PLATFORM[p].label}
                </button>
              ))}
            </div>
          )}
          <FormField label="Название" hint="Как его различать: страна, команда, для чего">
            <input className={INPUT} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Airbnb · Польша" />
          </FormField>
          <FormField label="Логин (почта или телефон)">
            <input className={INPUT} value={login} onChange={(e) => setLogin(e.target.value)} autoComplete="off" />
          </FormField>
          <FormField label="Пароль" hint={account ? (account.hasPassword ? 'Оставьте пустым, чтобы не менять' : 'Пароль ещё не задан') : undefined}>
            <div className="relative">
              <input
                className={cn(INPUT, 'pr-10')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                className="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-md border-0 bg-transparent p-0 text-muted-foreground hover:bg-secondary hover:text-heading"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </FormField>
          <FormField label="Заметка" hint="Видна всем, кому выдан доступ: чья карта привязана, лимиты, особенности">
            <textarea className={TEXTAREA} value={note} onChange={(e) => setNote(e.target.value)} />
          </FormField>
          {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-[13px] text-destructive">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Отмена</Button>
          <Button size="sm" disabled={saving} onClick={() => void submit()}>{saving ? 'Сохраняю…' : 'Сохранить'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
